import { Flex, Box, Text } from '@chakra-ui/react'
import { toMoney } from '@sequencehq/utils'
import {
  MagicTable,
  MagicTableCell,
  MagicTableEmptyState,
  MagicTableFilterConfig,
  MagicTableFilterEmptyState,
  RowSpanningRule,
  useLinkMagicTableWithSearchParams,
  useMagicTableInfiniteQuery
} from '@sequencehq/tables'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { useState } from 'react'
import { useSelectedCurrency } from '../../utils/useSelectedCurrency'
import { useSelectedDateRange } from '../../utils/useSelectedDateRange'
import { format } from '@sequencehq/utils/dates'
import { Currency } from '@sequencehq/api/utils/commonEnums'
import { useNavigate } from 'react-router-dom'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import {
  IdentificationIcon,
  RectangleStackIcon as SolidRectangleStackIcon
} from '@heroicons/react/24/solid'
import { humanize } from 'inflection'
import { RevRecDashboardDateFilter } from './RevRecDashboardDateFilter'
import { RevRecDashboardCurrencyFilter } from './RevRecDashboardCurrencyFilter'
import { CellContext } from '@tanstack/react-table'
import { transformJournalData } from './transformJournalData'
import { JournalDetailOverviewDrawer } from './JournalDetailOverviewDrawer'
import { isSamePeriod } from 'RevenueRecognition/view/utils/isSamePeriod'
import { InvoiceLinkCell } from 'RevenueRecognition/view/common/InvoiceLinkCell'
import { ProductLinkCell } from 'RevenueRecognition/view/common/ProductLinkCell'
import { useLoadAllCustomersWithAliases } from 'components/UsageEvents/useLoadAllCustomersWithAliases'
import { CubeIcon } from '@heroicons/react/16/solid'
import { useLoadAllItemsQuery } from '@sequencehq/api'
import {
  dashboardv99990101Client,
  type Dashboardv99990101Api
} from '@sequencehq/api/dist/clients/dashboard/v99990101'
import { WithPagination } from '@sequencehq/api/dashboard/v20240509'
import { RevRecDashboardStatsCard } from './RevRecDashboardStatsCard'

type Journal =
  Dashboardv99990101Api.GetJournals.GetJournalsResponse['journals'][number]

export type RevRecTableJournalEntry = {
  id: string
  journalId: string
  period: string
  customer?: {
    id: string
    name: string
  }
  product?: {
    id: string
    name: string
  }
  invoice?: {
    id: string
    name: string
  }
  journalType?: 'MANUAL' | 'SYSTEM'
  ledgerAccount: {
    name: string
    debit?: number
    credit?: number
  }
}

type JournalFilters = {
  currency?: string[]
  dateFrom?: string
  dateTo?: string
  customerId?: string[]
  productId?: string[]
}

type JournalView = {
  model: RevRecTableJournalEntry
  controlFilters: JournalFilters
  propFilters: Record<string, unknown>
}

export type RevRecTableGroupBy = 'customer' | 'product' | 'period' | undefined

const columnOrderByGroup: Record<string, string[]> = {
  customer: [
    'customer',
    'period',
    'product',
    'invoice',
    'ledger',
    'debit',
    'credit'
  ],
  product: [
    'product',
    'period',
    'customer',
    'invoice',
    'ledger',
    'debit',
    'credit'
  ],
  period: [
    'period',
    'customer',
    'product',
    'invoice',
    'ledger',
    'debit',
    'credit'
  ]
}

const getOrderedColumns = (
  selectedCurrency: Currency | null,
  groupBy: RevRecTableGroupBy
) => {
  const baseColumns = [
    {
      id: 'period',
      header: 'Period',
      accessorFn: (row: RevRecTableJournalEntry) =>
        format(new Date(row.period), 'MMMM yyyy'),
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => (
        <MagicTableCell
          text={value.getValue<string>()}
          textAlign={value.column.columnDef?.meta?.textAlign}
        />
      )
    },
    {
      id: 'customer',
      header: 'Customer',
      accessorFn: (row: RevRecTableJournalEntry) => row.customer?.name,
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => {
        const customer = value.row.original.customer
        if (!customer) {
          return null
        }
        return (
          <Flex
            alignItems="center"
            justifyContent="space-between"
            height="40px"
            pl="6px"
          >
            <CustomerPreviewCardPill customerId={customer.id} variant="ghost" />
          </Flex>
        )
      }
    },
    {
      id: 'product',
      header: 'Product',
      accessorFn: (row: RevRecTableJournalEntry) => row.product?.name,
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => {
        const product = value.row.original.product

        return <ProductLinkCell product={product} />
      }
    },
    {
      id: 'invoice',
      header: 'Invoice',
      accessorFn: (row: RevRecTableJournalEntry) => row.invoice?.name,
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => {
        const invoice = value.row.original.invoice

        return <InvoiceLinkCell invoice={invoice} />
      }
    },
    {
      id: 'ledger',
      header: 'Ledger',
      accessorFn: (row: RevRecTableJournalEntry) => row.ledgerAccount.name,
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => (
        <MagicTableCell text={humanize(value.getValue<string>())} />
      )
    },
    {
      id: 'debit',
      header: 'Debit',
      accessorFn: (row: RevRecTableJournalEntry) => {
        const totalDebit = row.ledgerAccount.debit
        return {
          currencyCode: selectedCurrency as Currency,
          debitValue: totalDebit
        }
      },
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => {
        const { currencyCode, debitValue } = value.getValue<{
          currencyCode: Currency
          debitValue: number
        }>()

        return (
          <MagicTableCell
            text={toMoney({
              currency: currencyCode,
              value: debitValue?.toString()
            })}
            textAlign="right"
          />
        )
      },
      meta: { textAlign: 'right' as const }
    },
    {
      id: 'credit',
      header: 'Credit',
      accessorFn: (row: RevRecTableJournalEntry) => {
        const totalCredit = row.ledgerAccount.credit
        return {
          currencyCode: selectedCurrency as Currency,
          creditValue: totalCredit
        }
      },
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => {
        const { currencyCode, creditValue } = value.getValue<{
          currencyCode: Currency
          creditValue: number
        }>()

        return (
          <MagicTableCell
            text={toMoney({
              currency: currencyCode,
              value: creditValue?.toString()
            })}
            textAlign="right"
          />
        )
      },
      meta: { textAlign: 'right' as const }
    }
  ]

  if (!groupBy) {
    return baseColumns
  }

  const currentOrder = columnOrderByGroup[groupBy]
  return baseColumns.sort((a, b) => {
    const aIndex = currentOrder.indexOf(a.id)
    const bIndex = currentOrder.indexOf(b.id)
    return aIndex - bIndex
  })
}

const useRevRecDashboardTableFilters = () => {
  const { customersWithAliases } = useLoadAllCustomersWithAliases()
  const { data: productsData } = useLoadAllItemsQuery(
    dashboardv99990101Client.getProducts
  )

  const products = productsData?.pages.flatMap(page => page?.items ?? [])

  const filtersConfig: MagicTableFilterConfig<JournalFilters>[] = [
    {
      type: 'multiSelect',
      paramName: 'customerId',
      label: 'Customer',
      icon: IdentificationIcon,
      options:
        customersWithAliases?.map(customer => ({
          value: customer.id,
          label: customer.customerName
        })) ?? [],
      format: (value: string) => <Text>{value}</Text>
    },
    {
      type: 'multiSelect',
      paramName: 'productId',
      label: 'Product',
      icon: CubeIcon,
      options:
        products?.map(product => ({
          value: product.id,
          label: product.name
        })) ?? [],
      format: (value: string) => <Text>{value}</Text>
    }
  ]

  const { activeFilters, onChangeActiveFilters, sortBy, onChangeSortBy } =
    useLinkMagicTableWithSearchParams(filtersConfig, [
      'startDate',
      'endDate',
      'currency'
    ])

  return {
    activeFilters,
    onChangeActiveFilters,
    sortBy,
    onChangeSortBy,
    filtersConfig
  }
}

export const RevRecDashboardTable = () => {
  const navigate = useNavigate()
  const { currency: selectedCurrency } = useSelectedCurrency()
  const { dateRange } = useSelectedDateRange()
  const [groupBy, setGroupBy] = useState<RevRecTableGroupBy>('period')
  const [selectedJournalId, setSelectedJournalId] = useState<string | null>(
    null
  )

  const {
    activeFilters,
    onChangeActiveFilters,
    sortBy,
    onChangeSortBy,
    filtersConfig
  } = useRevRecDashboardTableFilters()

  const { infiniteQuery } = useMagicTableInfiniteQuery(
    dashboardv99990101Client.getJournalsPaginated,
    filtersConfig,
    activeFilters,
    sortBy,
    100,
    100,
    {
      from: dateRange[0] ? format(dateRange[0], 'yyyy-MM-dd') : '',
      to: dateRange[1] ? format(dateRange[1], 'yyyy-MM-dd') : '',
      currency: selectedCurrency ?? 'USD'
    }
  )

  const columns = getOrderedColumns(selectedCurrency, groupBy)

  const rowSpanningRules: RowSpanningRule<RevRecTableJournalEntry>[] = [
    {
      columnId: 'period',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        if (groupBy === 'period') {
          return isSamePeriod(row1.period, row2.period)
        }
        return (
          row1.journalId === row2.journalId &&
          isSamePeriod(row1.period, row2.period)
        )
      }
    },
    {
      columnId: 'product',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        if (groupBy === 'product') {
          return row1.product?.id === row2.product?.id
        }
        return (
          row1.journalId === row2.journalId &&
          row1.product?.id === row2.product?.id
        )
      }
    },
    {
      columnId: 'customer',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        if (groupBy === 'customer') {
          return row1.customer?.id === row2.customer?.id
        }

        return (
          row1.journalId === row2.journalId &&
          row1.customer?.id === row2.customer?.id
        )
      }
    },
    {
      columnId: 'ledger',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        return (
          row1.id === row2.id &&
          row1.ledgerAccount.name === row2.ledgerAccount.name &&
          row1.invoice?.id === row2.invoice?.id
        )
      }
    },
    {
      columnId: 'invoice',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        return (
          row1.journalId === row2.journalId &&
          row1.invoice?.id === row2.invoice?.id
        )
      }
    },
    {
      columnId: 'debit',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        return (
          row1.id === row2.id &&
          row1.ledgerAccount.debit === row2.ledgerAccount.debit
        )
      }
    },
    {
      columnId: 'credit',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        return (
          row1.id === row2.id &&
          row1.ledgerAccount.credit === row2.ledgerAccount.credit
        )
      }
    }
  ]

  return (
    <Box position="relative" height="100%">
      <JournalDetailOverviewDrawer
        journalId={selectedJournalId}
        selectedCurrency={selectedCurrency}
        closeDrawer={() => setSelectedJournalId(null)}
      />
      <CurrentUserId>
        {userId => (
          <MagicTable<JournalView>
            key={groupBy}
            filters={filtersConfig}
            activeFilters={activeFilters}
            onChangeActiveFilters={onChangeActiveFilters}
            entityNamePlural="journals"
            sortBy={sortBy}
            onChangeSortBy={onChangeSortBy}
            columns={columns}
            additionalHeaderRows={
              <RevRecDashboardStatsCard
                hasResults={
                  infiniteQuery.isPending ||
                  (infiniteQuery.data?.pages[0]?.items || []).length !== 0
                }
              />
            }
            sequenceUserId={userId}
            infiniteQuery={
              infiniteQuery as unknown as {
                data?:
                  | {
                      pages: WithPagination<{
                        items: readonly RevRecTableJournalEntry[]
                      }>[]
                    }
                  | null
                  | undefined
                isFetching: boolean
                hasNextPage: boolean
                isFetchingNextPage: boolean
                fetchNextPage: () => void
              }
            }
            onRowClick={row => {
              setSelectedJournalId(row.journalId)
            }}
            enableColumnReordering={false}
            additionalToolbarComponentsV3={{
              start: <RevRecDashboardDateFilter />,
              end: <RevRecDashboardCurrencyFilter />
            }}
            emptyStateUnfiltered={entityNamePlural => (
              <MagicTableEmptyState
                entityNamePlural={entityNamePlural}
                imageSrc="/img/empty/invoices.png"
                learnMoreText="Learn more about revenue recognition journals"
                learnMoreHref="https://docs.sequencehq.com/revenue-recognition"
              />
            )}
            emptyStateFiltered={(entityNamePlural, clearFilters) => (
              <MagicTableFilterEmptyState
                entityNamePlural={entityNamePlural}
                entityIcon={SolidRectangleStackIcon}
                actionClearFilters={clearFilters}
                actionViewAll={() => navigate('/revenue-recognition')}
                variant="TABLE"
              />
            )}
            grouping={{
              onGroupBy: columnId => {
                setGroupBy(columnId as RevRecTableGroupBy)
              },
              rowSpanningRules,
              groupableColumns: ['customer', 'product', 'period'],
              groupBy,
              transformGroupData: (
                rows: RevRecTableJournalEntry[],
                groupBy?: string
              ) => {
                return transformJournalData(
                  // Data that is coming in from infinite query is actually an array of journals
                  // we do however want the type for this to be the same as the row type.
                  // This all happens because the data coming from API is different than what we want to have as a view model.
                  // We can't do this transformation outside of MT because it has to encompass all the rows fetched
                  // and that is taken care of by infiniteQuery inside of MT :V
                  rows as unknown as Journal[],
                  groupBy as RevRecTableGroupBy
                )
              },
              // For every fetched journal, there are 2 entries so the results size is doubled
              resultSizeMultiplier: 2,
              highlightRowGroup: row => row.journalId,
              getGroupKey: row => {
                switch (groupBy) {
                  case 'customer':
                    return row.customer?.id ?? row.id
                  case 'product':
                    return row.product?.id ?? row.id
                  case 'period': {
                    const date = new Date(row.period)
                    return `${date.getFullYear()}-${date.getMonth()}`
                  }
                  default:
                    return row.id
                }
              }
            }}
          />
        )}
      </CurrentUserId>
    </Box>
  )
}
